/**
 * Tela de Login - Atomic.Digital
 * Design moderno com cores laranja (#FF6B35) e preto (#000000)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  InputAdornment,
  IconButton,
  Fade,
  Slide,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  Google as GoogleIcon
} from '@mui/icons-material';
import atomicLogo from '../assets/atomic-logo.svg';
import { useAuth } from '../contexts/AuthContext';
import WhatsAppModal from '../components/Auth/WhatsAppModal';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signInWithGoogle, loading: authLoading, isAdmin, isApproved } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [hasCheckedUser, setHasCheckedUser] = useState(false);

  // Resetar verificação quando usuário deslogar
  useEffect(() => {
    if (!user) {
      setHasCheckedUser(false);
      setShowWhatsAppModal(false);
    }
  }, [user]);

  // Redirecionar se já estiver logado
  useEffect(() => {
    const checkUserStatus = async () => {
      // Só executar se:
      // 1. Usuário está logado
      // 2. Autenticação já terminou de carregar
      // 3. Ainda não verificamos este usuário
      if (!user || authLoading || hasCheckedUser) {
        console.log('⏸️ Aguardando:', { 
          temUsuario: !!user, 
          authLoading, 
          jaVerificou: hasCheckedUser 
        });
        return;
      }

      console.log('🔍 LoginPage - Verificando status do usuário:', user.email);
      console.log('📊 Status atual do contexto:', { isAdmin, isApproved });
      setHasCheckedUser(true);
      
      // Verificar se usuário tem WhatsApp cadastrado
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/user/${user.uid}`
        );
        
        if (!response.ok) {
          throw new Error('Erro ao buscar usuário');
        }
        
        const userData = await response.json();
        
        console.log('👤 Dados do usuário completos:', {
          email: userData.email,
          whatsapp: userData.whatsapp,
          isApproved: userData.isApproved,
          isAdmin: userData.isAdmin
        });
        
        // Verificar se precisa cadastrar WhatsApp
        if (!userData.whatsapp) {
          console.log('📱 Usuário sem WhatsApp - abrindo modal');
          setShowWhatsAppModal(true);
          return;
        }
        
        // Usuário tem WhatsApp, verificar aprovação usando dados do backend
        const userIsAdmin = userData.isAdmin === 1 || userData.isAdmin === true;
        const userIsApproved = userData.isApproved === 1 || userData.isApproved === true;
        
        console.log('🔐 Verificando permissões:', { userIsAdmin, userIsApproved });
        
        if (userIsAdmin) {
          console.log('✅ Admin detectado - redirecionando para /app');
          navigate('/app');
        } else if (userIsApproved) {
          console.log('✅ Usuário aprovado - redirecionando para /app');
          navigate('/app');
        } else {
          console.log('⏳ Usuário não aprovado - redirecionando para /pending-approval');
          navigate('/pending-approval');
        }
      } catch (error) {
        console.error('❌ Erro ao verificar status do usuário:', error);
        // Em caso de erro, redirecionar baseado apenas em isAdmin/isApproved do contexto
        if (isAdmin || isApproved) {
          console.log('✅ Usando dados do contexto - redirecionando para /app');
          navigate('/app');
        } else {
          console.log('⏳ Usando dados do contexto - redirecionando para /pending-approval');
          navigate('/pending-approval');
        }
      }
    };

    checkUserStatus();
  }, [user, navigate, isAdmin, isApproved, authLoading, hasCheckedUser]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Por enquanto, login manual não está implementado com autenticação real
    // Então apenas avisa o usuário para usar Google
    setError('Por favor, use o login com Google para acessar a plataforma');
    
    // Futura implementação: autenticação com email/senha
    // await signInWithEmailAndPassword(email, password);
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      setHasCheckedUser(false); // Resetar para permitir nova verificação
      await signInWithGoogle();
      // A navegação será feita automaticamente pelo useEffect quando user mudar
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer login com Google');
      setLoading(false);
      setHasCheckedUser(false); // Resetar em caso de erro também
    }
  };

  const handleWhatsAppSubmit = async (whatsapp: string) => {
    if (!user) {
      console.error('❌ Erro: usuário não está logado');
      return;
    }

    try {
      setLoading(true);
      console.log('📱 Salvando WhatsApp:', whatsapp);
      
      // Atualizar WhatsApp no backend
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/sync`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uid: user.uid,
            email: user.email || '',
            displayName: user.displayName || '',
            photoURL: user.photoURL || '',
            whatsapp: whatsapp
          })
        }
      );
      
      if (!response.ok) {
        throw new Error('Erro ao salvar WhatsApp');
      }
      
      const result = await response.json();
      console.log('✅ WhatsApp salvo com sucesso:', result);
      
      setShowWhatsAppModal(false);
      setHasCheckedUser(false); // Permitir nova verificação após salvar
      
      // Redirecionar baseado em aprovação
      if (isAdmin) {
        console.log('✅ Redirecionando admin para /app');
        navigate('/app');
      } else if (isApproved) {
        console.log('✅ Redirecionando usuário aprovado para /app');
        navigate('/app');
      } else {
        console.log('⏳ Redirecionando para aguardar aprovação');
        navigate('/pending-approval');
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar WhatsApp:', error);
      setError('Erro ao enviar WhatsApp. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%)',
        }}
      >
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Elementos decorativos de fundo */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,107,53,0.15) 0%, rgba(255,107,53,0) 70%)',
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          left: '-5%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,107,53,0.1) 0%, rgba(255,107,53,0) 70%)',
          filter: 'blur(60px)',
        }}
      />

      <Container maxWidth="sm" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
        <Fade in={true} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              p: 5,
              width: '100%',
              borderRadius: 4,
              background: 'rgba(26, 26, 26, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 107, 53, 0.1)',
              boxShadow: '0 8px 32px 0 rgba(255, 107, 53, 0.2)',
            }}
          >
            <Slide direction="down" in={true} timeout={600}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                {/* Logo */}
                <Box
                  component="img"
                  src={atomicLogo}
                  alt="Atomic.Digital"
                  sx={{
                    height: 60,
                    mb: 3,
                    filter: 'drop-shadow(0 4px 8px rgba(255, 107, 53, 0.3))',
                  }}
                />
                
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    mb: 1,
                    letterSpacing: '0.5px',
                  }}
                >
                  Bem-vindo de volta
                </Typography>
                
                <Typography
                  variant="body1"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.95rem',
                  }}
                >
                  Faça login para acessar a plataforma
                </Typography>
              </Box>
            </Slide>

            <Slide direction="up" in={true} timeout={800}>
              <Box>
                {/* Alerta de erro */}
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}

                {/* Botão de Login com Google */}
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<GoogleIcon />}
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  sx={{
                    py: 1.8,
                    mb: 3,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#FFFFFF',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: '#FF6B35',
                      backgroundColor: 'rgba(255, 107, 53, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255, 255, 255, 0.3)',
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: '#FF6B35' }} /> : 'Continuar com Google'}
                </Button>

                <Divider sx={{ my: 3 }}>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem' }}>
                    ou
                  </Typography>
                </Divider>

                <form onSubmit={handleLogin}>
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    type="email"
                    label="E-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined sx={{ color: '#FF6B35' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#FFFFFF',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 107, 53, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#FF6B35',
                          borderWidth: 2,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-focused': {
                          color: '#FF6B35',
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 4 }}>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    label="Senha"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined sx={{ color: '#FF6B35' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#FFFFFF',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 107, 53, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#FF6B35',
                          borderWidth: 2,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-focused': {
                          color: '#FF6B35',
                        },
                      },
                    }}
                  />
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    py: 1.8,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #FF6B35 0%, #FF8A5B 100%)',
                    boxShadow: '0 4px 20px 0 rgba(255, 107, 53, 0.4)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF8A5B 0%, #FF6B35 100%)',
                      boxShadow: '0 6px 30px 0 rgba(255, 107, 53, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                  }}
                >
                  Entrar
                </Button>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '0.85rem',
                    }}
                  >
                    Ainda não tem uma conta?{' '}
                    <Typography
                      component="span"
                      sx={{
                        color: '#FF6B35',
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Cadastre-se
                    </Typography>
                  </Typography>
                </Box>
              </form>
              </Box>
            </Slide>
          </Paper>
        </Fade>
      </Container>

      {/* Modal de WhatsApp */}
      <WhatsAppModal 
        open={showWhatsAppModal}
        onSubmit={handleWhatsAppSubmit}
        loading={loading}
      />
    </Box>
  );
};

export default LoginPage;
